import { Filter } from "lucide-react";
import { FolderManagement } from "@/components/knowledge-base";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Agent } from "@/types/agent/agent";
import { DocumentSource } from "@/types/knowledge/knowledge-base";
import { useAuth } from "@/auth/AuthProvider";

export interface DocumentFilterState {
  selectedGenieId: string;
  selectedFolderId: string | null | undefined;
  selectedSourceType: string | undefined;
}

export interface DocumentFilterControlsProps extends DocumentFilterState {
  agents: Partial<Agent>[];
  hasActiveFilters: boolean;
  onGenieChange: (id: string) => void;
  onFolderChange: (id: string | null | undefined) => void;
  onSourceTypeChange: (type: string | undefined) => void;
  onClear: () => void;
}

const sourceTypeOptions = [
  { value: DocumentSource.Pdf, label: "PDF Upload" },
  { value: DocumentSource.Upload, label: "Document Upload" },
  { value: DocumentSource.Audio, label: "Audio Transcription" },
  { value: DocumentSource.Video, label: "Video Transcription" },
  { value: DocumentSource.Website, label: "Website" },
  { value: DocumentSource.YouTube, label: "YouTube" },
  { value: DocumentSource.Note, label: "Notes" },
  { value: DocumentSource.Scrape, label: "Scraped Website" },
  { value: DocumentSource.QuickResearch, label: "Quick Research" },
  { value: DocumentSource.DeepResearch, label: "Deep Research" },
];

const GenieSelect = ({
  selectedGenieId,
  agents,
  onGenieChange,
}: Pick<DocumentFilterControlsProps, "selectedGenieId" | "agents" | "onGenieChange">) => {
  const { user } = useAuth();

  const isTeamAgent = (agent: Partial<Agent>) => agent.user_id !== user?.id;

  return (
    <Select value={selectedGenieId} onValueChange={onGenieChange}>
      <SelectTrigger>
        <SelectValue placeholder="All Genies" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All Genies</SelectItem>
        {agents.map((agent) => (
          <SelectItem key={agent.id} value={agent.id || ""} className="flex items-center justify-between">
            <div className="flex items-center justify-between w-full gap-2">
              <span>{agent.name}</span>
              {isTeamAgent(agent) && (
                <span className="text-xs text-gray-400 ml-auto">Team</span>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const SourceTypeSelect = ({
  selectedSourceType,
  onSourceTypeChange,
}: Pick<DocumentFilterControlsProps, "selectedSourceType" | "onSourceTypeChange">) => (
  <Select
    value={selectedSourceType || "all"}
    onValueChange={(value) => onSourceTypeChange(value === "all" ? undefined : value)}
  >
    <SelectTrigger>
      <SelectValue placeholder="All Doc Types" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">All Doc Types</SelectItem>
      {sourceTypeOptions.map((option) => (
        <SelectItem key={option.value} value={option.value}>
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const DesktopFilterControls = ({
  selectedGenieId,
  selectedSourceType,
  agents,
  hasActiveFilters,
  onGenieChange,
  onSourceTypeChange,
  onClear,
}: Omit<DocumentFilterControlsProps, "selectedFolderId" | "onFolderChange">) => (
  <div className="hidden sm:flex items-center gap-3">
    <div className="text-sm text-gray-700">Genie:</div>
    <div className="sm:min-w-[180px] min-w-0">
      <GenieSelect
        selectedGenieId={selectedGenieId}
        agents={agents}
        onGenieChange={onGenieChange}
      />
    </div>
    <div className="text-sm text-gray-700">Type:</div>
    <div className="sm:min-w-[180px] min-w-0">
      <SourceTypeSelect
        selectedSourceType={selectedSourceType}
        onSourceTypeChange={onSourceTypeChange}
      />
    </div>
    {hasActiveFilters && (
      <Button variant="outline" size="sm" onClick={onClear}>
        Clear
      </Button>
    )}
  </div>
);

export const MobileFilterSheet = ({
  selectedGenieId,
  selectedFolderId,
  selectedSourceType,
  agents,
  hasActiveFilters,
  onGenieChange,
  onFolderChange,
  onSourceTypeChange,
  onClear,
}: DocumentFilterControlsProps) => (
  <Sheet>
    <SheetTrigger asChild>
      <Button variant="outline" size="sm" className="flex items-center gap-2">
        <Filter className="h-4 w-4" />
        Filter
      </Button>
    </SheetTrigger>
    <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
      <SheetHeader>
        <SheetTitle>Organize & Filter</SheetTitle>
      </SheetHeader>
      <div className="mt-4 space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Folders</label>
          <FolderManagement selectedFolderId={selectedFolderId} onFolderSelect={onFolderChange} />
        </div>

        <div className="space-y-2 pt-4 border-t">
          <label className="text-sm font-medium text-gray-700">Filter by Genie</label>
          <GenieSelect
            selectedGenieId={selectedGenieId}
            agents={agents}
            onGenieChange={onGenieChange}
          />
        </div>

        <div className="space-y-2 pt-4 border-t">
          <label className="text-sm font-medium text-gray-700">Filter by Source Type</label>
          <SourceTypeSelect
            selectedSourceType={selectedSourceType}
            onSourceTypeChange={onSourceTypeChange}
          />
        </div>

        {hasActiveFilters && (
          <Button variant="outline" size="sm" className="w-full" onClick={onClear}>
            Clear All Filters
          </Button>
        )}
      </div>
    </SheetContent>
  </Sheet>
);
